import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

const credentials = [
  { icon: '🏆', title: '法国蓝带厨艺学院', subtitle: '高级西点师认证' },
  { icon: '🎓', title: '国家高级西点师', subtitle: '职业资格认证' },
  { icon: '⭐', title: '20年+教学经验', subtitle: '培养学员5000+' },
  { icon: '🏅', title: '多项国际大赛奖项', subtitle: '专业认可' },
]

const timeline = [
  { year: '2004', event: '法国蓝带厨艺学院毕业' },
  { year: '2006', event: '进入沈阳香格里拉大酒店担任西点主厨' },
  { year: '2010', event: '参加法国世界杯西点大赛获得银奖' },
  { year: '2012', event: '创立自己的烘焙工作室' },
  { year: '2015', event: '开始西点教育事业' },
  { year: '2020', event: '线上教学平台上线' },
  { year: '2024', event: '已培养超过5000名学员' },
]

const philosophies = [
  {
    icon: '💝',
    title: '用心教学',
    description: '对待每一位学员都如同家人，用耐心和爱心点燃他们对烘焙的热情。',
  },
  {
    icon: '🎯',
    title: '精益求精',
    description: '追求技术的极致，每一个小细节都不放过，培养工匠精神。',
  },
  {
    icon: '🌱',
    title: '因材施教',
    description: '根据每位学员的基础和目标，制定个性化的教学方案。',
  },
  {
    icon: '🤝',
    title: '终身支持',
    description: '毕业后仍可获得技术支持、配方更新和创业指导。',
  },
]

export default function About() {
  return (
    <div className="grain-overlay">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center bg-gradient-to-br from-cream via-cream to-champagne/10 overflow-hidden">
        <div className="absolute top-20 left-20 w-80 h-80 bg-caramel/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-champagne/20 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-2 bg-caramel/10 text-caramel rounded-full text-sm font-medium mb-6">
                关于王老师
              </span>
              <h1 className="font-display text-5xl md:text-6xl text-chocolate leading-tight mb-6">
                用双手创造
                <br />
                <span className="text-gradient">甜蜜事业</span>
              </h1>
              <p className="text-lg text-chocolate/70 mb-8 max-w-lg leading-relaxed">
                20年烘焙之路，从法国蓝带到五星级酒店，再到创办自己的烘焙课堂。
                我热爱这份事业，更热爱看到学员们通过学习实现自己的烘焙梦想。
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary">
                  预约课程
                </Link>
                <a href="#timeline" className="px-8 py-3 border-2 border-caramel text-caramel rounded-lg font-medium hover:bg-caramel hover:text-white transition-all">
                  了解更多
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-champagne to-caramel rounded-3xl blur-xl opacity-30" />
                <div className="relative rounded-3xl overflow-hidden aspect-square">
                  <img src="/images/teacher-portrait.jpg" alt="王老师" className="w-full h-full object-cover" />
                </div>
              </div>
              {/* Floating badge */}
              <motion.div
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-4"
              >
                <div className="text-champagne text-4xl mb-1">⭐⭐⭐⭐⭐</div>
                <p className="text-sm text-chocolate">5000+学员推荐</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-4xl text-chocolate mb-4">资质认证</h2>
            <p className="text-chocolate/60 text-lg">专业认可，品质保障</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {credentials.map((cred, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-cream rounded-2xl p-6 text-center card-hover"
              >
                <div className="text-5xl mb-4">{cred.icon}</div>
                <h3 className="font-display text-lg text-chocolate mb-1">{cred.title}</h3>
                <p className="text-sm text-caramel">{cred.subtitle}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section id="timeline" className="py-20 bg-gradient-to-b from-cream to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-4xl text-chocolate mb-4">成长历程</h2>
            <p className="text-chocolate/60 text-lg">20年烘焙之路的每一步</p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-caramel/20" />

            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center mb-8 ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                  <div className="bg-white rounded-2xl p-6 shadow-lg inline-block">
                    <span className="font-display text-2xl text-caramel font-bold">{item.year}</span>
                    <p className="text-chocolate mt-2">{item.event}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-caramel rounded-full border-4 border-white shadow" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Teaching Philosophy */}
      <section className="py-20 bg-chocolate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-4xl text-cream mb-4">教学理念</h2>
            <p className="text-cream/60 text-lg">不只是教授技术，更是传递热爱</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {philosophies.map((philosophy, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-cream/10 backdrop-blur-sm rounded-2xl p-8 flex gap-6"
              >
                <div className="text-5xl">{philosophy.icon}</div>
                <div>
                  <h3 className="font-display text-xl text-cream mb-3">{philosophy.title}</h3>
                  <p className="text-cream/70 leading-relaxed">{philosophy.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-caramel to-champagne">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl text-white mb-6">成为王老师的学生</h2>
          <p className="text-white/80 text-lg mb-8">
            立即报名，开启您的烘焙之旅
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-caramel rounded-lg font-semibold hover:bg-cream transition-colors"
          >
            预约咨询
          </Link>
        </div>
      </section>
    </div>
  )
}